<template>
  <div class="header">
    <div class="container-fluid">
      <div class="card mt-5">
        <div class="card-header d-flex justify-content-between align-items-center">
          <span class="mt-3 fs-2">Notification Template</span>
        </div>

        <div>
          <b-tabs v-model="activeTabIndex" content-class="mt-3" justified @input="onTabChange">
            <b-tab v-for="(tab, index) in tabs" :key="tab.code" :title="tab.title" />
          </b-tabs>
        </div>

        <div class="d-flex justify-content-end align-items-center m-3 gap-2">
          <div class="d-flex justify-content-center align-items-center gap-3">
            <label class="align-self-center m-0">Search</label>
            <b-form-input class="w-auto" v-model="filters.search" placeholder="Type Here..." @keyup.enter="onSearch" />
          </div>
          <div class="col-lg-2">
            <v-select
              v-model="filters.tenant"
              :options="tenantOptions"
              :reduce="(e) => e.id"
              label="name"
              placeholder="Select Tenant"
            />
          </div>
          <div class="col-lg-2">
            <v-select
              v-model="filters.module_type_id"
              :options="moduleTypeOptions"
              :reduce="(e) => e.id"
              label="name"
              placeholder="Select Module"
            />
          </div>

          <b-button variant="primary" @click="clearFilter">
            <span class="fe fe-x-square"></span>
            Clear Filter
          </b-button>
        </div>

        <NotificationTemplateTable
          :rows="notificationTemplates.data"
          :total="notificationTemplates.total"
          :showing-from="showingFrom"
          :showing-to="showingTo"
          :page="filters.page"
          :page-size="filters.pageSize"
          @sort-change="onSortChange"
          @page-change="onPageChange"
          @per-page-change="onPerPageChange"
          @page-change-from-bv="onPageChangeFromBv"
          @update="updateData"
          @change-status="updateStatus"
        />

        <NotificationTemplateModal
          :modal-id="'notification-template-modal'"
          :is-edit="isEdit"
          :tenant-options="tenantOptions"
          :data="modalData"
          @save="onSaveModal"
          @preview="onPreviewModal"
          @close="showModal = false"
        />

        <NotificationTemplatePreviewModal ref="previewModal" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import NotificationTemplateModal from "./notification-template-modal.vue";
import NotificationTemplateTable from "./notification-template-table.vue";
import NotificationTemplatePreviewModal from "./notification-template-preview-modal.vue";
import toast from "@/utils/toast";

export default {
  name: "NotificationTemplateMain",

  components: {
    NotificationTemplateModal,
    NotificationTemplateTable,
    NotificationTemplatePreviewModal,
  },

  data() {
    return {
      filters: {
        page: 1,
        pageSize: 10,
        order: "desc",
        field: "updated_at",
        tenant: null,
        search: "",
        channel_code: "email",
        module_type_id: null,
      },
      tabs: [
        { title: "Email", code: "email" },
        { title: "Whatsapp", code: "whatsapp" },
        { title: "In-Web", code: "in-web" },
      ],
      activeTabIndex: 0,
      isLoading: false,
      tenantOptions: [
        { id: "bib-academy", name: "BIB Academy" },
        { id: "ugems-university", name: "UGEMS University" },
      ],
      _searchTimer: null,
      showModal: false,
      isEdit: false,
      selectedTemplateId: null,
      modalData: null,
      moduleTypeOptions: [],
    };
  },

  computed: {
    ...mapGetters("notificationTemplate", ["notificationTemplates"]),

    showingFrom() {
      if (!this.notificationTemplates.total) return 0;
      return (this.filters.page - 1) * this.filters.pageSize + 1;
    },

    showingTo() {
      const to = this.filters.page * this.filters.pageSize;
      return to > this.notificationTemplates.total ? this.notificationTemplates.total : to;
    },
  },

  watch: {
    filters: {
      deep: true,
      handler(newFilters) {
        this.updateUrlQuery(newFilters);
      },
    },

    "filters.tenant"() {
      this.filters.page = 1;
      this.loadData();
    },

    "filters.module_type_id"() {
      this.filters.page = 1;
      this.loadData();
    },

    "$route.query": {
      handler() {
        this.syncFiltersFromQuery();
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions("notificationTemplate", [
      "getNotificationTemplates",
      "getNotificationTemplateDetail",
      "updateNotificationTemplate",
      "previewNotificationTemplate",
      "getModuleTypes",
    ]),

    cleanObject(obj) {
      return Object.fromEntries(
        Object.entries(obj).filter(
          ([, v]) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)
        )
      );
    },

    buildQueryFromFilters() {
      const raw = {
        page: this.filters.page,
        pageSize: this.filters.pageSize,
        order: this.filters.order,
        field: this.filters.field,
        tenant: this.filters.tenant,
        module_type_id: this.filters.module_type_id,
        search: this.filters.search,
        all: this.filters.all,
        channel_code: this.filters.channel_code,
      };
      return this.cleanObject(raw);
    },

    async loadData() {
      this.isLoading = true;

      try {
        const query = this.buildQueryFromFilters();
        await this.getNotificationTemplates(query);
        if (this.notificationTemplates.page) this.filters.page = this.notificationTemplates.page;
        if (this.notificationTemplates.limit) this.filters.pageSize = this.notificationTemplates.limit;
      } finally {
        this.isLoading = false;
      }
    },

    onTabChange(newIndex) {
      const selectedTab = this.tabs[newIndex];
      if (!selectedTab) return;

      this.filters.channel_code = selectedTab.code;
      this.filters.page = 1;
      this.loadData();
    },

    onPageChange(payload) {
      let page = null;
      if (typeof payload === "number") page = payload;
      else if (payload?.currentPage !== undefined) page = payload.currentPage;
      else if (payload?.currentPageNumber !== undefined) page = payload.currentPageNumber;
      if (page === null) return;
      this.filters.page = page;
      this.loadData();
    },

    onPageChangeFromBv(page) {
      this.filters.page = page;
      this.loadData();
    },

    onPerPageChange(payload) {
      let per = null;
      if (typeof payload === "number") per = payload;
      else if (payload?.currentPerPage !== undefined) per = payload.currentPerPage;
      else if (payload?.perPage !== undefined) per = payload.perPage;
      if (per === null) return;
      this.filters.pageSize = per;
      this.filters.page = 1;
      this.loadData();
    },

    onSortChange(params) {
      if (!Array.isArray(params) || params.length === 0) return;
      const { field, type } = params[0] || {};
      const isReset = type === "none";
      this.filters = {
        ...this.filters,
        field: isReset ? null : field,
        order: isReset ? null : type || "asc",
        page: 1,
      };
      this.loadData();
    },

    onSearch() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.loadData();
      }, 500);
    },

    clearFilter() {
      this.filters = { ...this.filters, search: "", tenant: null, page: 1, module_type_id: null };
      this.loadData();
    },

    async updateData(row) {
      this.isEdit = true;
      this.selectedTemplateId = row.id;
      this.showModal = true;

      const detail = await this.getNotificationTemplateDetail(row.id);
      this.modalData = detail;

      this.$nextTick(() => {
        this.$bvModal.show("notification-template-modal");
      });
    },

    async viewDetails(row) {
      this.isEdit = false;
      this.selectedTemplateId = row.id;
      this.showModal = true;

      const detail = await this.getNotificationTemplateDetail(row.id);
      this.modalData = detail;

      this.$nextTick(() => {
        this.$bvModal.show("notification-template-modal");
      });
    },

    updateUrlQuery(filters) {
      const query = this.cleanObject({
        ...this.$route.query,
        page: filters.page,
        pageSize: filters.pageSize,
        order: filters.order,
        field: filters.field,
        tenant: filters.tenant,
        module_type_id: filters.module_type_id,
        search: filters.search,
        channel_code: filters.channel_code,
      });

      const current = JSON.stringify(this.$route.query);
      const next = JSON.stringify(query);

      if (current !== next) {
        this.$router.replace({ query }).catch(() => {});
      }
    },

    syncFiltersFromQuery() {
      const query = this.$route.query;
      const parsed = {
        page: Number(query.page) || 1,
        pageSize: Number(query.pageSize) || 10,
        order: query.order || null,
        field: query.field || null,
        tenant: query.tenant || null,
        search: query.search || "",
        channel_code: query.channel_code || "email",
      };

      this.filters = { ...this.filters, ...parsed };

      const idx = this.tabs.findIndex((t) => t.code === this.filters.channel_code);
      this.activeTabIndex = idx >= 0 ? idx : 0;
    },

    async onSaveModal(data) {
      try {
        if (this.isEdit) {
          await this.updateNotificationTemplate({
            id: this.selectedTemplateId,
            data,
          });

          toast({ type: "success", title: "Success", message: "Template notifikasi berhasil diperbarui" });
        }

        this.$bvModal.hide("notification-template-modal");
        this.loadData();
      } catch (error) {
        const msg =
          error?.response?.data?.error || error?.message || "Terjadi kesalahan while saving notification template.";

        toast({ type: "error", title: "Error", message: msg });
      }
    },

    async onPreviewModal(data) {
      try {
        const res = await this.previewNotificationTemplate({ id: data.id, data: { body: data.body } });

        const { type, body } = res?.data?.data || {};

        this.$refs.previewModal.open({
          type,
          body,
        });
      } catch (error) {
        const msg =
          error?.response?.data?.error || error?.message || "Terjadi kesalahan while saving notification template.";

        toast({ type: "error", title: "Error", message: msg });
      }
    },

    async updateStatus(row) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: "Apakah Anda yakin ingin memperbarui status?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, perbarui",
        cancelButtonText: "Batal",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await this.updateNotificationTemplate({ id: row.id, data: { is_active: !row.is_active } });

            toast({ type: "success", title: "Success", message: "Status template berhasil diperbarui" });
          } catch (error) {
            const msg = error?.response?.data?.error || error?.message || "Status belum berhasil diperbarui.";

            toast({ type: "error", title: "Error", message: msg });
          } finally {
            this.loadData();
          }
        }
      });
    },

    async loadModuleTypes() {
      try {
        const moduleTypes = await this.getModuleTypes();
        this.moduleTypeOptions = moduleTypes;
      } catch (err) {
        console.error("Failed to load triggers:", err);
      }
    },
  },

  async mounted() {
    this.syncFiltersFromQuery();

    await this.loadModuleTypes();
    await this.loadData();
  },

  beforeDestroy() {
    clearTimeout(this._searchTimer);
  },
};
</script>

<style scoped>
.table-header {
  white-space: nowrap;
}

.w-action {
  width: 140px;
  min-width: 140px;
  max-width: 140px;
}

.vgt-table.table-auto table {
  table-layout: auto !important;
}
</style>
